import { For, Show } from "solid-js";

import { entityDetailLink } from "~/components/entityDetailLink";
import { useAuditLog, useMe } from "~/lib/hooks";
import type { AuditLogRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

/**
 * Per-user activity feed for the dashboard. Hits the audit log filtered to
 * the current user's actor_id. Click-through reuses the shared
 * entityDetailLink helper.
 *
 * Falls back gracefully when the actor is a system/service caller (no
 * user_id on `Me`) — that path won't have any actor_id-scoped audit rows
 * to show anyway.
 *
 * Note: the audit-log endpoint is admin-only. For non-admin users this
 * widget will get a 403 and renders a friendly "not available" state
 * rather than an error.
 */
export function MyRecentChangesWidget(props: Props) {
  const me = useMe();
  const audit = useAuditLog(props.wsKey, () => ({
    actor_id: me.data?.user_id ?? undefined,
    limit: 10,
  }));

  const items = () => audit.data?.items ?? [];

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <h3 class="card-title text-base">Your recent changes</h3>

        <Show when={audit.isError}>
          <p class="text-sm opacity-60 mt-2">Audit log isn't available with your role.</p>
        </Show>

        <Show when={audit.isLoading}>
          <p role="status" aria-live="polite" class="text-sm opacity-60 mt-2">
            Loading…
          </p>
        </Show>

        <Show when={!audit.isLoading && !audit.isError && items().length === 0}>
          <p class="text-sm opacity-60 mt-2">No changes yet.</p>
        </Show>

        <ul class="mt-2 divide-y divide-base-200">
          <For each={items()}>{(row) => <Row row={row} wsKey={props.wsKey} />}</For>
        </ul>
      </div>
    </div>
  );
}

function Row(props: { row: AuditLogRead; wsKey: () => string }) {
  const label = `${props.row.entity_type} ${props.row.entity_id.slice(0, 8)}…`;
  const link = entityDetailLink(
    props.wsKey(),
    props.row.entity_type,
    props.row.entity_id,
    <span class="font-medium">{label}</span>,
    props.row.object_key,
  );
  return (
    <li class="py-1.5 flex items-center gap-2 text-sm">
      <span
        class={`badge badge-sm shrink-0 ${
          props.row.action === "create"
            ? "badge-success"
            : props.row.action === "archive" || props.row.action === "delete"
              ? "badge-error"
              : "badge-outline"
        }`}
      >
        {props.row.action}
      </span>
      <span class="flex-1 truncate">{link ?? <span class="font-medium">{label}</span>}</span>
      <span class="text-xs opacity-60 shrink-0">{relativeTime(props.row.created_at)}</span>
    </li>
  );
}

/** "5m ago", "2h ago", "yesterday", "Mar 4". Plain, no i18n dep. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return "yesterday";
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
